import Highcharts from 'highcharts';

/**
 * Configure Highcharts to use the given IANA timezone for datetime axis rendering.
 * Highcharts v12+ uses the native `time.timezone` option (IANA string) instead of
 * the removed `time.getTimezoneOffset` callback.
 *
 * Call this once per chart component before building the options object.
 */
export const setHighchartsTimezone = (timezone: string): void => {
  Highcharts.setOptions({
    time: {
      timezone,
    },
  });
};

/**
 * Mobile touch-scroll fix for Highcharts v13.
 *
 * Root cause: Highcharts' Pointer.touch() calls e.preventDefault() on touchstart
 * and touchmove events, which blocks page scrolling. However, we still want the
 * tooltip to follow the finger (Highcharts' internal tracking logic).
 *
 * Fix: Monkey-patch Event.prototype.preventDefault so that when called on touch
 * events whose target is inside a .highcharts-container, it becomes a no-op.
 * This allows Highcharts' touch handlers to run normally (tooltip follows finger)
 * but removes the scroll-blocking side effect.
 *
 * Trade-off: Pinch-to-zoom on charts is disabled on mobile (the browser handles
 * pinch as page zoom instead). This is acceptable since our charts don't use
 * zooming.
 */

/**
 * Touch device detection — defense-in-depth with three independent checks:
 *
 * 1. `'ontouchstart' in window` — Touch Events API is available in this browser.
 * 2. `navigator.maxTouchPoints > 0` — hardware reports touch capability (covers
 *    PointerEvents-only browsers where 'ontouchstart' may be absent).
 * 3. `window.matchMedia('(hover: none)').matches` — the PRIMARY input device has
 *    no hover capability. This is the crucial discriminator that excludes desktop
 *    touchscreen laptops (Surface Pro, etc.) where the primary input is a
 *    mouse/trackpad. On those devices, maxTouchPoints > 0 is true but (hover: none)
 *    is false — without this check, the patch would incorrectly activate.
 *
 * All three together ensure the patch only runs on phones/tablets where touch is
 * the primary interaction and scrolling over charts is the expected gesture.
 */
const isTouchDevice =
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || navigator.maxTouchPoints > 0) &&
  window.matchMedia('(hover: none)').matches;

if (isTouchDevice && typeof document !== 'undefined') {
  // oxlint-disable-next-line typescript/unbound-method -- intentional: capturing prototype method for monkey-patch, called with correct this via .call()
  const origPreventDefault = Event.prototype.preventDefault;

  Event.prototype.preventDefault = function (this: Event): void {
    // Only intercept touch events targeting Highcharts containers
    if (
      (this.type === 'touchstart' || this.type === 'touchmove') &&
      // Touch targets on Highcharts SVG charts are always Element instances
      // (SVGElement extends Element). If a Text node is somehow the target,
      // we correctly fall through to the original preventDefault — Highcharts'
      // Pointer.touch() only operates on container Elements, not raw text nodes.
      this.target instanceof Element &&
      this.target.closest('.highcharts-container')
    ) {
      // No-op: let the browser handle the gesture (scroll) while Highcharts
      // still processes the event for tooltip/crosshair tracking
      return;
    }
    origPreventDefault.call(this);
  };
}
